import React, {forwardRef, useEffect, useRef, useState} from 'react';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './checkbox.component.module.styl';
import CheckboxSvg from './svg';

import type {ComponentProps, ReactNode} from 'react';
import mergeRefs from 'merge-refs';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


type NativeProps = Omit<ComponentProps<'input'>, 'ref' | 'onChange'>;

export interface CheckBoxProps extends NativeProps {
    /**
     * `name` attribute forwarded to the underlying `<input>` element. Also
     * passed as the second argument to the deprecated `onChange` callback.
     */
    name?: string,
    /**
     * Controls the checked state of the checkbox. When combined with
     * `onCheckBoxChange`, the component becomes fully controlled.
     */
    checked?: boolean,

    /**
     * When `true`, renders the checkbox without any label or inner SVG markup —
     * useful for custom checkbox appearances built entirely with CSS.
     */
    custom?: boolean,

    /**
     * When `true`, the checkbox is non-interactive and styled as disabled.
     * Forwarded to the underlying `<input disabled>` attribute.
     */
    disabled?: boolean,

    /**
     * When `true`, renders the label text with a link-like underline style.
     * Use when the label itself is clickable and navigates somewhere.
     */
    highlightedLabel?: boolean,

    /**
     * When `true`, renders the checkbox in the indeterminate state (dash icon)
     * — typically used for "select all" checkboxes where only some children are
     * selected. The underlying `<input>` element's `indeterminate` DOM property
     * is set imperatively.
     */
    indeterminate?: boolean,

    /**
     * When `true`, applies semibold font weight to the label, visually
     * distinguishing parent/header checkboxes in a hierarchical list.
     */
    main?: boolean,

    /**
     * Called on each change event with `(checked, indeterminate, name)`.
     * Prefer `onCheckBoxChange` for new code.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use onCheckBoxChange
     */
    onChange?: (checked: boolean, indeterminate: boolean, name: string) => void,

    /**
     * Called on each change event with the native `ChangeEvent` and the `name`
     * prop. Use this instead of the deprecated `onChange`.
     */
    onCheckBoxChange?: (changeData: {
        event: React.ChangeEvent<HTMLInputElement>,
        name: string,
    }) => void,

    /**
     * When `true`, the component does not update its own internal checked state
     * on click — only the `onChange`/`onCheckBoxChange` callback fires. Use for
     * fully controlled scenarios where the parent manages the checked state.
     */
    usePropsOnly?: boolean,

    /**
     * When `true`, pointer events pass through the checkbox element to underlying
     * elements. Useful inside drag-and-drop containers where the checkbox should
     * not block drag interactions.
     */
    clickTrough?: boolean,

    /**
     * Label content rendered to the right of the checkbox. Accepts any React node
     * — text, icons, or formatted JSX. When `ellipsisType` is `"END"` (default),
     * long labels are truncated with an ellipsis tooltip.
     */
    label?: ReactNode | string,

    /**
     * Additional CSS class names applied to the root wrapper `<div>`.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,

    /**
     * Additional CSS class names applied to the checkbox pointer `<span>`.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixPointer?: string,
    /**
     * Additional CSS class names applied to the `<label>` box wrapper.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixBox?: string,
    /**
     * Additional CSS class names applied to the label text element.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixLabel?: string,
    /**
     * Additional CSS class names applied to the `<input>` handler element.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mixHandler?: string,

    /**
     * Controls how long labels are truncated:
     * - `"END"` (default) — label is wrapped in `EllipsisTextHint` for single-line
     *   truncation with a native browser tooltip.
     * - `"NONE"` — label is rendered as-is with no truncation applied.
     */
    ellipsisType?: EllipsisType,
    /**
     * Native `<div>` props spread onto the root wrapper element.
     */
    rootProps?: ComponentProps<'div'>,
    /**
     * Ref to the root wrapper `<div>` element.
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Native `<label>` props spread onto the inner label element.
     */
    labelProps?: ComponentProps<'label'>,
    /**
     * Ref to the inner `<label>` element.
     */
    labelRef?: React.Ref<HTMLLabelElement>,
    /**
     * Forwarded ref to the underlying `<input type="checkbox">` element.
     */
    ref?: React.Ref<HTMLInputElement>,
}

/**
 * Ellipsis behavior for the checkbox label.
 * - `END` — truncates long labels with an overflow ellipsis and tooltip (default).
 * - `NONE` — renders the label without any truncation.
 */
export const EllipsisTypes = {
    END: 'END',
    NONE: 'NONE',
} as const;


type EllipsisType = keyof typeof EllipsisTypes;


/**
 * Tri-state checkbox (unchecked / checked / indeterminate) with an optional
 * label and built-in overflow truncation.
 *
 * Use `checked` + `onCheckBoxChange` for a fully controlled checkbox. Set
 * `indeterminate` for a "select all" header that reflects a partial selection.
 * Pass `usePropsOnly` when the parent should own all state transitions.
 *
 * ## Accessibility
 * Implements the WAI-ARIA checkbox role. Pairs natively with a `<label>`
 * element via the `label` prop. For indeterminate "select all" patterns, the
 * `indeterminate` DOM property is set imperatively after render.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/forms-checkbox--check-box-default}
 */
export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(function CheckBox(
    props, ref) {
    const {
        checked = false,
        onChange = () => undefined,
        onCheckBoxChange = () => undefined,
        disabled = false,
        highlightedLabel = false,
        indeterminate = false,
        usePropsOnly = false,
        custom = false,
        main = false,
        name = '',
        ellipsisType = EllipsisTypes.END,
        label,
        clickTrough,
        mix,
        mixPointer,
        mixBox,
        mixLabel,
        mixHandler,
        rootProps,
        rootRef,
        labelProps,
        labelRef,
        ...inputProps
    } = props;

    const [isChecked, setIsChecked] = useState<boolean>(checked);
    const [isIndeterminate, setIsIndeterminate] = useState<boolean>(indeterminate);
    const [isPressed, setIsPressed] = useState<boolean>(false);

    const innerRef = useRef<HTMLInputElement>(null);

    const setIndeterminate = () => {
        if (innerRef && innerRef.current) {
            innerRef.current.indeterminate = isIndeterminate;
        }
    };

    useEffect(() => {
        setIndeterminate();
    }, [isIndeterminate]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsIndeterminate(indeterminate);
    }, [indeterminate]);

    useEffect(() => {
        setIsChecked(Boolean(checked));
    }, [checked]);

    const onMouseDown = () => {
        if (!disabled) {
            setIsPressed(true);
        }
    };

    const onMouseOut = () => {
        if (!disabled) {
            setIsPressed(false);
        }
    };

    const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {checked: eventChecked, indeterminate: eventIndeterminate} = event.currentTarget;

        if (!usePropsOnly) {
            setIsChecked(eventChecked);
            setIsIndeterminate(false);
        }

        onChange?.(eventChecked, eventIndeterminate, name || '');
        onCheckBoxChange?.({
            event,
            name: name || '',
        });
    };

    const componentClasses = classNames(
        {
            'checkbox-button_custom': custom,
            'checkbox-button_main': main,
            'checkbox-button_click-through': clickTrough,
            'checkbox-button_disabled': disabled,
        },
        'checkbox-button',
        mix,
        rootProps?.className,
    );

    const handlerClasses = classNames(
        'checkbox-button__handler',
        mixHandler,
        inputProps.className,
        {
            'checkbox-button__handler_pressed': isPressed,
            'checkbox-button__handler_checked': isChecked,
            'checkbox-button__handler_disabled': disabled,
        },
    );

    const checkboxClasses = classNames(
        'checkbox-button__pointer',
        mixPointer,
        {
            'checkbox-button__pointer_only': !label && !custom,
        },
    );

    const labelClasses = classNames('checkbox-button__label', mixLabel, {
        'checkbox-button__label_highlighted': highlightedLabel,
    });

    let ellipsisLabel = null;

    if (label && ellipsisType === EllipsisTypes.END) {
        ellipsisLabel = <EllipsisTextHint mix={labelClasses}>{label}</EllipsisTextHint>;
    } else if (label && ellipsisType === EllipsisTypes.NONE) {
        ellipsisLabel = label;
    }

    const boxClassNames = classNames('checkbox-button__box', mixBox, labelProps?.className);

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={componentClasses}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <label
                ref={labelRef}
                {...labelProps}
                className={boxClassNames}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseOut}
                onBlur={onMouseOut}>
                <input
                    {...inputProps}
                    ref={mergeRefs(innerRef, ref)}
                    className={handlerClasses}
                    disabled={disabled}
                    checked={isChecked}
                    onChange={onHandleChange}
                    name={name}
                    type="checkbox"
                />
                <div className={classes['checkbox-button__caption']}>
                    <span className={checkboxClasses}>
                        {CheckboxSvg({
                            disabled,
                            checked: isChecked,
                            indeterminate: isIndeterminate,
                            pressed: isPressed,
                        }, custom)}
                    </span>
                    {ellipsisLabel}
                </div>
            </label>
        </div>
    );
});

CheckBox.displayName = 'CheckBox';
